import React, { useState, useEffect } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Switch,
  FormControlLabel
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TutorialContentItem from './TutorialContentItem';

const AdminTutorialModule = ({ 
  module, 
  expanded, 
  onToggle, 
  targetRequiredMap, 
  onRequiredChange, 
  isEditingMode = false 
}) => {
  // State for managing child module expansion
  const [expandedChild, setExpandedChild] = useState([]);
  const [readyToInteract, setReadyToInteract] = useState(true); // Always ready in admin mode
  
  // Set ready to interact immediately in admin mode
  useEffect(() => {
    setReadyToInteract(true);
  }, []);
  
  // Handle child module expansion
  const handleChildChange = (childId) => (event, isExpanded) => {
    if (!readyToInteract) return;
    
    if (isExpanded) {
      setExpandedChild(prev => prev.includes(childId) ? prev : [...prev, childId]);
    } else {
      setExpandedChild(prev => prev.filter(item => item !== childId));
    }
  };

  // Create a wrapper component for content items with required/optional toggle
  const AdminContentItem = ({ content }) => {
    const isRequired = targetRequiredMap[content.itemId] !== undefined 
      ? targetRequiredMap[content.itemId] 
      : false;

    // In editing mode, just show the content item without required/optional toggle
    if (isEditingMode) {
      return (
        <Box sx={{ mb: 1 }}>
          <TutorialContentItem content={content} hideCompletionStatus={true} hideRequiredStatus={true} />
        </Box>
      );
    }

    return (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 2,
        mb: 1
      }}>
        <Box sx={{ flex: 1 }}>
          <TutorialContentItem content={content} hideCompletionStatus={true} hideRequiredStatus={true} />
        </Box>
        <FormControlLabel
          control={
            <Switch
              checked={isRequired}
              onChange={(e) => onRequiredChange(content.itemId, e.target.checked)}
              color="primary"
              size="small"
            />
          }
          label={isRequired ? 'Required' : 'Optional'}
          labelPlacement="start"
          sx={{ 
            ml: 1,
            minWidth: '120px',
            '& .MuiFormControlLabel-label': {
              fontSize: '0.875rem',
              fontWeight: isRequired ? 600 : 400,
              color: isRequired ? '#d32f2f' : '#666'
            }
          }}
        />
      </Box>
    );
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={onToggle}
      sx={{
        ...(module.parentId ? {
          // Child modules: no margins, overlapping borders
          margin: '0 !important',
          marginTop: '-1px !important',
          marginBottom: '0 !important',
          '&:first-of-type': {
            marginTop: '0 !important',
          },
        } : {
          // Top-level modules: card style with fully rounded corners, no shadow
          borderRadius: '16px !important',
          overflow: 'visible',
          backgroundColor: '#f7f7f7',
          border: 'none',
          boxShadow: 'none',
        }),
        '&:before': {
          display: 'none',
        },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          borderTopLeftRadius: !module.parentId ? '16px' : 0,
          borderTopRightRadius: !module.parentId ? '16px' : 0,
          backgroundColor: !module.parentId ? '#f7f7f7' : '#f6f6f6',
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          width: '100%',
          height: '100%',
          py: 0,
        }}>
          <Box sx={{ 
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            minHeight: '40px',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontFamily: 'Arial, sans-serif',
                  fontSize: module.parentId ? '1rem' : '1.2rem',
                  fontWeight: 700,
                  color: '#333',
                  lineHeight: 1.2,
                  mb: module.description ? 0.25 : 0,
                }}
              >
                {module.title}
              </Typography>
            </Box>
            {module.description && (
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#666',
                  lineHeight: 1.3,
                  fontSize: '0.875rem',
                }}
              >
                {module.description}
              </Typography>
            )}
          </Box>
          

        </Box>
      </AccordionSummary>
      
      <AccordionDetails
        sx={{
          borderBottomLeftRadius: !module.parentId ? '16px' : 0,
          borderBottomRightRadius: !module.parentId ? '16px' : 0,
          backgroundColor: !module.parentId ? '#f7f7f7' : '#f6f6f6',
        }}
      >
        <Box sx={{ 
          py: module.parentId ? 0 : 3,
          px: module.parentId ? 0 : 0,
        }}>
          {module.children && module.children.map((child) => {
            if (child.type === 'module') {
              return (
                <AdminTutorialModule
                  key={child.itemId}
                  module={child}
                  expanded={expandedChild.includes(child.itemId)}
                  onToggle={handleChildChange(child.itemId)}
                  targetRequiredMap={targetRequiredMap}
                  onRequiredChange={onRequiredChange}
                  isEditingMode={isEditingMode}
                />
              );
            } else if (child.type === 'content') {
              return (
                <AdminContentItem
                  key={child.itemId}
                  content={child}
                />
              );
            }
            return null;
          })}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default AdminTutorialModule;